import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedMover, updateSelectedMover } from "@/store/mover/slice";
import { useUpdateMoverMutation } from "@/store/mover/apis";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Material } from "@/types";
import { SaveIcon, TrashIcon } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
interface MaterialRowProps {
  material: Material;
  removed?: boolean;
  onRemove?: () => void;
}

const MaterialRow: React.FC<MaterialRowProps> = ({
  material,
  removed,
  onRemove,
}) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [updateMover, { isLoading }] = useUpdateMoverMutation();
  const [materialData, setMaterialData] = useState(material);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const handleUpdateMaterial = async () => {
    if (isLoading) return;

    try {
      const updatedMover = await updateMover({
        data: {
          materials_attributes: [
            {
              id: material.id,
              name: materialData.name,
              description: materialData.description,
              price: materialData.price,
            },
          ],
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Success",
        description: "Material updated successfully.",
      });
    } catch (error) {
      console.error("Error updating material:", error);
      toast({
        title: "Error",
        description: "Failed to update material. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRemove = () => {
    return (
      <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {material.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-black">
              Please confirm that you want to remove this material from your
              list of available materials (you can always add it back).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 text-white hover:bg-red-500/90"
              onClick={() => onRemove?.()}
            >
              Yes, remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  return (
    <div className="grid grid-cols-12 gap-4 items-start mt-4">
      <div className="col-span-3">
        <Input
          id={`name-${material.id}`}
          value={materialData.name}
          onChange={(e) =>
            setMaterialData((prev) => ({ ...prev, name: e.target.value }))
          }
          disabled={removed}
          placeholder="Name"
        />
      </div>
      <div className="col-span-4">
        <Input
          id={`description-${material.id}`}
          value={materialData.description || ""}
          onChange={(e) =>
            setMaterialData((prev) => ({
              ...prev,
              description: e.target.value,
            }))
          }
          disabled={removed}
          placeholder="Description"
        />
      </div>
      <div className="col-span-2">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-700 text-sm">
            $
          </span>
          <Input
            id={`price-${material.id}`}
            type="number"
            defaultValue={parseFloat(
              (materialData.price || "0").toString()
            ).toFixed(2)}
            onChange={(e) =>
              setMaterialData((prev) => ({
                ...prev,
                price: parseFloat(e.target.value),
              }))
            }
            disabled={removed}
            className="pl-6"
            onFocus={(e) => e.target.select()}
          />
        </div>
      </div>

      {!removed && (
        <div className="col-span-3 flex items-end h-full gap-2">
          <Button
            variant="link"
            className="text-black px-0"
            onClick={handleUpdateMaterial}
          >
            <SaveIcon className="w-4 h-4 mr-1" />
            Save Changes
          </Button>
          <Button
            variant="link"
            className="text-red-700"
            onClick={() => setShowRemoveConfirm(true)}
          >
            <TrashIcon className="w-4 h-4 mr-1" />
            Remove
          </Button>
        </div>
      )}

      {handleRemove()}
    </div>
  );
};

const Materials: React.FC = () => {
  const dispatch = useDispatch();
  const selectedMover = useSelector(getSelectedMover);
  const { toast } = useToast();
  const [updateMoverApi, { isLoading }] = useUpdateMoverMutation();
  const [newMaterial, setNewMaterial] = useState<Partial<Material>>({
    name: "",
    description: "",
    price: undefined,
  });

  if (!selectedMover) return null;

  const handleAddMaterial = async () => {
    if (isLoading) return;
    if (!selectedMover) return;

    try {
      const updatedMover = await updateMoverApi({
        data: {
          materials_attributes: [
            {
              name: newMaterial.name,
              description: newMaterial.description,
              price: newMaterial.price,
            },
          ],
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      setNewMaterial({
        name: "",
        description: "",
        price: undefined,
      });

      toast({
        title: "Success",
        description: "Material added successfully.",
      });
    } catch (error) {
      console.error("Error adding material:", error);
      toast({
        title: "Error",
        description: "Failed to add material. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveMaterial = async (material: Material) => {
    if (!selectedMover) return;

    try {
      const updatedMover = await updateMoverApi({
        data: {
          materials_attributes: [
            {
              id: material.id,
              _destroy: true,
            },
          ],
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Success",
        description: "Material removed successfully.",
      });
    } catch (error) {
      console.error("Error removing material:", error);
      toast({
        title: "Error",
        description: "Failed to remove material. Please try again.",
        variant: "destructive",
      });
    }
  };

  const materials = selectedMover.materials || [];

  return (
    <div className="px-8 py-4 max-w-screen-md">
      <h2 className="text-xl font-bold">Materials ({materials.length})</h2>
      {/* <div className="mt-4 w-full text-sm font-bold text-stone-700">
        Add New Material
      </div> */}

      <div className="mt-4 grid grid-cols-12 gap-2 items-start">
        <div className="col-span-3">
          <Input
            id="new-name"
            placeholder="Name"
            value={newMaterial.name}
            onChange={(e) =>
              setNewMaterial((prev) => ({ ...prev, name: e.target.value }))
            }
          />
        </div>
        <div className="col-span-4">
          <Input
            id="new-description"
            placeholder="Description"
            value={newMaterial.description || ""}
            onChange={(e) =>
              setNewMaterial((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
          />
        </div>
        <div className="col-span-2">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-700 text-sm">
              $
            </span>
            <Input
              id="new-price"
              type="number"
              value={newMaterial.price || ""}
              onChange={(e) =>
                setNewMaterial((prev) => ({
                  ...prev,
                  price: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                }))
              }
              className="pl-6"
            />
          </div>
        </div>
        <div className="col-span-3 flex items-end h-full">
          <Button size="sm" onClick={handleAddMaterial}>
            Add Material
          </Button>
        </div>
      </div>

      <Separator className="my-4" />

      <div>
        {/* <div className="bg-stone-100 text-black px-2 py-1 text-sm">
          Materials ({materials.length})
        </div> */}
        {[...materials]
          .sort((a, b) => b.created_at.localeCompare(a.created_at))
          .map((material) => (
            <MaterialRow
              key={material.id}
              material={material}
              onRemove={() => handleRemoveMaterial(material)}
            />
          ))}
      </div>
    </div>
  );
};

export default Materials;
